import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function apiErrorMessage(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, user } = useAuth();
  const [mode, setMode] = useState(location.state?.mode === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [devLink, setDevLink] = useState("");
  const [checkInboxFor, setCheckInboxFor] = useState("");

  // If already signed in, bounce out.
  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/", { replace: true });
  }, [user, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data.user);
      toast.success(`Welcome back, ${r.data.user.name?.split(" ")[0] || "there"}!`);
      navigate(r.data.user.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setDevLink("");
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { email, password, name });
      if (r.data.email_verified) {
        toast.success("Password added — signing you in…");
        // Password linked to a pre-verified Google account → just log in.
        const login = await api.post("/auth/login", { email, password });
        setUser(login.data.user);
        navigate(login.data.user.role === "admin" ? "/admin" : "/", { replace: true });
      } else {
        setCheckInboxFor(email);
        if (r.data.dev_verification_url) setDevLink(r.data.dev_verification_url);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // "Check your inbox" success state after signup
  if (checkInboxFor) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4 py-12" data-testid="auth-check-inbox">
        <div className="w-full max-w-md text-center">
          <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-sky-100 dark:bg-sky-950/40 grid place-items-center">
            <Mail className="w-6 h-6 text-sky-700 dark:text-sky-300" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Check your inbox</h1>
          <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-1">
            We sent a verification link to
          </p>
          <p className="font-medium text-slate-900 dark:text-slate-100 mb-6">{checkInboxFor}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
            Click the link in that email to confirm your account, then come back here to sign in.
          </p>
          {devLink && (
            <div data-testid="auth-dev-verification-link" className="text-left mb-6 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg">
              <div className="text-[11px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold mb-1">Dev mode</div>
              <p className="text-xs text-slate-700 dark:text-slate-300 mb-2">Resend isn't configured, so use this link:</p>
              <Link to={devLink.replace(window.location.origin, "")}
                    className="text-xs text-sky-700 dark:text-sky-300 hover:underline break-all">
                {devLink}
              </Link>
            </div>
          )}
          <button
            data-testid="auth-resend-verification"
            onClick={async () => {
              try {
                await api.post("/auth/resend-verification", { email: checkInboxFor });
                toast.success("Verification email sent again.");
              } catch (err) { toast.error(apiErrorMessage(err)); }
            }}
            className="text-sm text-sky-700 dark:text-sky-300 hover:underline"
          >
            Resend verification email
          </button>
          <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">
            <button onClick={() => { setCheckInboxFor(""); setMode("signin"); }}
                    className="text-slate-700 dark:text-slate-300 hover:underline" data-testid="auth-back-to-signin">
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-md bg-sky-700 grid place-items-center text-white font-heading font-bold text-lg">H</div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {mode === "signin" ? "Sign in to keep learning" : "Bookmark blogs and follow pathways"}
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex mb-6 rounded-md bg-slate-100 dark:bg-slate-800 p-1">
          <button
            data-testid="auth-tab-signin"
            onClick={() => { setMode("signin"); setError(""); }}
            className={`flex-1 py-2 text-sm font-medium rounded transition-colors ${mode === "signin" ? "bg-white dark:bg-slate-900 shadow text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400 hover:text-slate-700"}`}
          >
            Sign in
          </button>
          <button
            data-testid="auth-tab-signup"
            onClick={() => { setMode("signup"); setError(""); }}
            className={`flex-1 py-2 text-sm font-medium rounded transition-colors ${mode === "signup" ? "bg-white dark:bg-slate-900 shadow text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400 hover:text-slate-700"}`}
          >
            Sign up
          </button>
        </div>

        {/* Google button */}
        <button
          data-testid="auth-google-btn"
          onClick={googleLogin}
          type="button"
          className="w-full mb-4 inline-flex items-center justify-center gap-3 px-4 py-2.5 rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-50 text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-800"/></div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500 uppercase tracking-widest">or</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={mode === "signin" ? handleSignIn : handleSignUp} className="space-y-4">
          {mode === "signup" && (
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Your name (optional)</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">Password</label>
              {mode === "signin" && (
                <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs text-sky-700 dark:text-sky-300 hover:underline">
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500"/>
              <input
                data-testid="auth-password-input"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "Minimum 8 characters" : "Your password"}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                className="w-full pl-10 pr-10 py-2.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
              />
              <button
                type="button"
                data-testid="auth-password-toggle"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
            {mode === "signup" && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">At least 8 characters. Pick something unique.</p>
            )}
          </div>

          {error && (
            <div data-testid="auth-error" className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-sm font-medium disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
            {mode === "signin" ? "Sign in" : "Create account"}
            {!busy && <ArrowRight className="w-4 h-4"/>}
          </button>
        </form>

        <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-6">
          {mode === "signin" ? (
            <>New here? <button onClick={() => { setMode("signup"); setError(""); }} className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="auth-switch-signup">Create an account</button></>
          ) : (
            <>Already have an account? <button onClick={() => { setMode("signin"); setError(""); }} className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="auth-switch-signin">Sign in</button></>
          )}
        </p>
      </div>
    </div>
  );
}
